import {
  parseSignatureAnchorUri,
  SignatureAnchorKind,
} from 'features/reporting/pdfGeneration/signatureAnchors'
import { SIGNOFF_ROLE_LABELS } from 'pages/reporting/shared/approval/approvalRules'
import {
  PDFArray,
  PDFDict,
  PDFDocument,
  PDFFont,
  PDFHexString,
  PDFImage,
  PDFName,
  PDFPage,
  PDFString,
  rgb,
  StandardFonts,
} from 'pdf-lib'
import { SignoffRole } from 'types'

import {
  CertificateSigner,
  fillCertificateSlot,
  isCertificatePage,
} from './certificatePage'

const CELL_PADDING = 3
const DATE_FONT_SIZE = 8
const INK = rgb(0.07, 0.09, 0.15)

/**
 * Stamps one signature into the PDF twice: as a signature image and date in
 * the cover page's reserved cells (what the client sees), and as a full entry
 * on the certificate page kept for internal audit.
 */
export async function stampSignature(
  sourcePdf: Uint8Array | ArrayBuffer,
  signatureDataUrl: string,
  signer: CertificateSigner,
  slotIndex: number
): Promise<Uint8Array> {
  if (!/^data:image\/(png|jpe?g);/.test(signatureDataUrl)) {
    throw new Error('Signature image must be PNG or JPEG')
  }

  const pdfDoc = await PDFDocument.load(sourcePdf)
  const fonts = {
    regular: await pdfDoc.embedFont(StandardFonts.Helvetica),
    bold: await pdfDoc.embedFont(StandardFonts.HelveticaBold),
  }
  const image = signatureDataUrl.startsWith('data:image/png')
    ? await pdfDoc.embedPng(signatureDataUrl)
    : await pdfDoc.embedJpg(signatureDataUrl)

  signCoverCells(pdfDoc, image, signer, fonts.regular)
  fillCertificateSlot(pdfDoc, image, signer, slotIndex, fonts)

  return pdfDoc.save()
}

/** Strips the audit certificate page and any unused signature cells for the client-facing copy. */
export async function prepareClientCopy(
  signedPdf: Uint8Array | ArrayBuffer
): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.load(signedPdf)
  pdfDoc
    .getPages()
    .map((page, index) => ({ page, index }))
    .filter(({ page }) => isCertificatePage(page))
    .reverse()
    .forEach(({ index }) => pdfDoc.removePage(index))
  for (const page of pdfDoc.getPages()) {
    for (const { ref } of findAnchors(pdfDoc, page)) {
      removeAnnotation(page, ref)
    }
  }
  return pdfDoc.save()
}

function signCoverCells(
  pdfDoc: PDFDocument,
  image: PDFImage,
  signer: CertificateSigner,
  font: PDFFont
) {
  const cells = pdfDoc.getPages().flatMap((page) =>
    findAnchors(pdfDoc, page)
      .filter((anchor) => anchor.role === signer.role)
      .map((anchor) => ({ page, ...anchor }))
  )
  const signatureCell = cells.find((cell) => cell.kind === 'signature')
  if (!signatureCell) {
    throw new Error(
      `This draft has no signature cell for ${SIGNOFF_ROLE_LABELS[signer.role]}. Regenerate the draft PDF, then sign again.`
    )
  }

  const inner = {
    x: signatureCell.rect.x + CELL_PADDING,
    y: signatureCell.rect.y + CELL_PADDING,
    width: signatureCell.rect.width - CELL_PADDING * 2,
    height: signatureCell.rect.height - CELL_PADDING * 2,
  }
  const scaled = image.scaleToFit(inner.width, inner.height)
  signatureCell.page.drawImage(image, {
    x: inner.x,
    y: inner.y + (inner.height - scaled.height) / 2,
    width: scaled.width,
    height: scaled.height,
  })

  const dateCell = cells.find((cell) => cell.kind === 'date')
  if (dateCell) {
    dateCell.page.drawText(signer.signedAt.slice(0, 10), {
      x: dateCell.rect.x + CELL_PADDING,
      y: dateCell.rect.y + (dateCell.rect.height - DATE_FONT_SIZE) / 2 + 1,
      size: DATE_FONT_SIZE,
      font,
      color: INK,
    })
  }

  for (const cell of cells) removeAnnotation(cell.page, cell.ref)
}

interface Anchor {
  ref: PDFDict
  kind: SignatureAnchorKind
  role: SignoffRole
  rect: { x: number; y: number; width: number; height: number }
}

/** Link annotations the cover page reserved for signatures (see SignatureAnchor). */
function findAnchors(pdfDoc: PDFDocument, page: PDFPage): Anchor[] {
  const annots = page.node.Annots()
  if (!annots) return []
  const anchors: Anchor[] = []
  for (let i = 0; i < annots.size(); i++) {
    const annot = pdfDoc.context.lookup(annots.get(i))
    if (!(annot instanceof PDFDict)) continue
    const action = pdfDoc.context.lookup(annot.get(PDFName.of('A')))
    const uri =
      action instanceof PDFDict
        ? pdfDoc.context.lookup(action.get(PDFName.of('URI')))
        : undefined
    if (!(uri instanceof PDFString || uri instanceof PDFHexString)) continue
    const parsed = parseSignatureAnchorUri(uri.decodeText())
    const rect = pdfDoc.context.lookup(annot.get(PDFName.of('Rect')))
    if (!parsed || !(rect instanceof PDFArray)) continue
    const [x1, y1, x2, y2] = rect.asArray().map((n) => Number(n.toString()))
    anchors.push({
      ref: annot,
      ...parsed,
      rect: {
        x: Math.min(x1, x2),
        y: Math.min(y1, y2),
        width: Math.abs(x2 - x1),
        height: Math.abs(y2 - y1),
      },
    })
  }
  return anchors
}

function removeAnnotation(page: PDFPage, annot: PDFDict) {
  const annots = page.node.Annots()
  if (!annots) return
  for (let i = annots.size() - 1; i >= 0; i--) {
    if (page.doc.context.lookup(annots.get(i)) === annot) annots.remove(i)
  }
}
